/**
 * 預處理狀態 Store
 *
 * 管理圖片預處理相關的狀態，包括：
 * - 原始圖片
 * - 參考線
 * - 覆蓋矩形（橡皮擦）
 * - 塗黑線條
 * - 偵測到的尺寸
 */

import { createStore } from './index';
import type { Point, Line, Rectangle, DetectedDimension } from '@/types';

// ========================================================================
// 狀態型別
// ========================================================================

/** 繪圖模式 */
export type DrawingMode = 'reference' | 'eraser' | 'blackline' | 'dimension-select' | 'none';

/** 端點類型 */
export type EndpointType = 'start' | 'end';

/** 參考線資料 */
export interface ReferenceLine extends Line {
  /** 實際長度（公尺） */
  lengthMeters: number;
}

export interface PreprocessState {
  /** 原始檔案 */
  originalFile: File | null;

  /** 原始檔案名稱 */
  originalFileName: string | null;

  /** 原始圖片 Blob */
  originalBlob: Blob | null;

  /** 原始圖片 Data URL */
  originalDataUrl: string | null;

  /** 處理後的圖片 Data URL */
  processedDataUrl: string | null;

  /** 當前繪圖模式 */
  drawingMode: DrawingMode;

  /** 參考線 */
  referenceLine: ReferenceLine | null;

  /** 是否正在繪製參考線 */
  isDrawingReferenceLine: boolean;

  /** 參考線起點（繪製中） */
  referenceLineStart: Point | null;

  /** 覆蓋矩形列表（橡皮擦功能） */
  coverRectangles: Rectangle[];

  /** 塗黑線條列表 */
  blackLines: Line[];

  /** 偵測到的尺寸標註 */
  detectedDimensions: DetectedDimension[];

  /** 是否正在偵測尺寸 */
  isDetectingDimensions: boolean;

  /** 目前選中的尺寸 ID */
  selectedDimensionId: number | null;

  /** 正在拖曳的端點 */
  draggingEndpoint: EndpointType | null;

  /** Canvas 縮放比例 */
  canvasScale: number;

  /** 圖片原始寬度 */
  imageWidth: number;

  /** 圖片原始高度 */
  imageHeight: number;
}

// ========================================================================
// 初始狀態
// ========================================================================

const initialState: PreprocessState = {
  originalFile: null,
  originalFileName: null,
  originalBlob: null,
  originalDataUrl: null,
  processedDataUrl: null,
  drawingMode: 'none',
  referenceLine: null,
  isDrawingReferenceLine: false,
  referenceLineStart: null,
  coverRectangles: [],
  blackLines: [],
  detectedDimensions: [],
  isDetectingDimensions: false,
  selectedDimensionId: null,
  draggingEndpoint: null,
  canvasScale: 1,
  imageWidth: 0,
  imageHeight: 0,
};

// ========================================================================
// Store 實例
// ========================================================================

export const preprocessStore = createStore<PreprocessState>(initialState);

// ========================================================================
// Actions - 圖片
// ========================================================================

/**
 * 設定原始圖片
 */
export function setOriginalImage(
  file: File,
  dataUrl: string,
  blob: Blob,
  width: number,
  height: number
): void {
  preprocessStore.setState({
    originalFile: file,
    originalFileName: file.name,
    originalBlob: blob,
    originalDataUrl: dataUrl,
    imageWidth: width,
    imageHeight: height,
    // 重設其他狀態
    processedDataUrl: null,
    referenceLine: null,
    coverRectangles: [],
    blackLines: [],
    detectedDimensions: [],
  });
}

/**
 * 設定處理後的圖片
 */
export function setProcessedImage(dataUrl: string): void {
  preprocessStore.setState({ processedDataUrl: dataUrl });
}

/**
 * 清除所有圖片資料
 */
export function clearImage(): void {
  preprocessStore.reset();
}

// ========================================================================
// Actions - 繪圖模式
// ========================================================================

/**
 * 設定繪圖模式
 */
export function setDrawingMode(mode: DrawingMode): void {
  preprocessStore.setState({
    drawingMode: mode,
    isDrawingReferenceLine: false,
    referenceLineStart: null,
  });
}

// ========================================================================
// Actions - 參考線
// ========================================================================

/**
 * 開始繪製參考線
 */
export function startDrawingReferenceLine(start: Point): void {
  preprocessStore.setState({
    isDrawingReferenceLine: true,
    referenceLineStart: start,
  });
}

/**
 * 完成參考線繪製
 */
export function finishReferenceLine(end: Point, lengthMeters: number): void {
  const state = preprocessStore.getState();
  if (!state.referenceLineStart) return;

  preprocessStore.setState({
    referenceLine: {
      start: state.referenceLineStart,
      end,
      lengthMeters,
    },
    isDrawingReferenceLine: false,
    referenceLineStart: null,
    drawingMode: 'none',
  });
}

/**
 * 取消參考線繪製
 */
export function cancelReferenceLine(): void {
  preprocessStore.setState({
    isDrawingReferenceLine: false,
    referenceLineStart: null,
  });
}

/**
 * 清除參考線
 */
export function clearReferenceLine(): void {
  preprocessStore.setState({
    referenceLine: null,
    isDrawingReferenceLine: false,
    referenceLineStart: null,
  });
}

// ========================================================================
// Actions - 覆蓋矩形（橡皮擦）
// ========================================================================

/**
 * 新增覆蓋矩形
 */
export function addCoverRectangle(rect: Rectangle): void {
  preprocessStore.setState((state) => ({
    coverRectangles: [...state.coverRectangles, rect],
  }));
}

/**
 * 移除最後一個覆蓋矩形
 */
export function undoLastCoverRectangle(): void {
  preprocessStore.setState((state) => ({
    coverRectangles: state.coverRectangles.slice(0, -1),
  }));
}

/**
 * 清除所有覆蓋矩形
 */
export function clearCoverRectangles(): void {
  preprocessStore.setState({ coverRectangles: [] });
}

// ========================================================================
// Actions - 塗黑線條
// ========================================================================

/**
 * 新增塗黑線條
 */
export function addBlackLine(line: Line): void {
  preprocessStore.setState((state) => ({
    blackLines: [...state.blackLines, line],
  }));
}

/**
 * 移除最後一個塗黑線條
 */
export function undoLastBlackLine(): void {
  preprocessStore.setState((state) => ({
    blackLines: state.blackLines.slice(0, -1),
  }));
}

/**
 * 清除所有塗黑線條
 */
export function clearBlackLines(): void {
  preprocessStore.setState({ blackLines: [] });
}

// ========================================================================
// Actions - 尺寸偵測
// ========================================================================

/**
 * 設定偵測到的尺寸
 */
export function setDetectedDimensions(dimensions: DetectedDimension[]): void {
  preprocessStore.setState({
    detectedDimensions: dimensions,
    isDetectingDimensions: false,
  });
}

/**
 * 設定是否正在偵測尺寸
 */
export function setIsDetectingDimensions(isDetecting: boolean): void {
  preprocessStore.setState({ isDetectingDimensions: isDetecting });
}

/**
 * 選取尺寸標註
 */
export function selectDimension(id: number | null): void {
  preprocessStore.setState({
    selectedDimensionId: id,
    draggingEndpoint: null,
  });
}

/**
 * 開始拖曳端點
 */
export function startDraggingEndpoint(endpoint: EndpointType): void {
  preprocessStore.setState({ draggingEndpoint: endpoint });
}

/**
 * 結束拖曳端點
 */
export function stopDraggingEndpoint(): void {
  preprocessStore.setState({ draggingEndpoint: null });
}

/**
 * 更新選中尺寸的端點位置
 */
export function updateSelectedDimensionEndpoint(endpoint: EndpointType, position: Point): void {
  const state = preprocessStore.getState();
  if (state.selectedDimensionId === null) return;

  const updatedDimensions = state.detectedDimensions.map((dim) => {
    if (dim.id === state.selectedDimensionId && dim.line) {
      return {
        ...dim,
        line: {
          ...dim.line,
          [endpoint]: position,
        },
      };
    }
    return dim;
  });

  preprocessStore.setState({ detectedDimensions: updatedDimensions });
}

/**
 * 將選中的尺寸設為參考線
 */
export function useSelectedDimensionAsReference(): boolean {
  const state = preprocessStore.getState();
  if (state.selectedDimensionId === null) return false;

  const selectedDimension = state.detectedDimensions.find(
    (dim) => dim.id === state.selectedDimensionId
  );

  if (!selectedDimension?.line) return false;

  preprocessStore.setState({
    referenceLine: {
      start: selectedDimension.line.start,
      end: selectedDimension.line.end,
      lengthMeters: selectedDimension.valueMeters,
    },
    selectedDimensionId: null,
    draggingEndpoint: null,
    drawingMode: 'none',
  });

  return true;
}

/**
 * 取得選中的尺寸
 */
export function getSelectedDimension(): DetectedDimension | null {
  const state = preprocessStore.getState();
  if (state.selectedDimensionId === null) return null;
  return state.detectedDimensions.find((dim) => dim.id === state.selectedDimensionId) ?? null;
}

// ========================================================================
// Actions - Canvas
// ========================================================================

/**
 * 設定 Canvas 縮放比例
 */
export function setCanvasScale(scale: number): void {
  preprocessStore.setState({ canvasScale: scale });
}

// ========================================================================
// Selectors
// ========================================================================

/**
 * 檢查是否有參考線
 */
export function hasReferenceLine(): boolean {
  return preprocessStore.getState().referenceLine !== null;
}

/**
 * 取得參考線長度（公尺）
 */
export function getReferenceLineLength(): number | null {
  return preprocessStore.getState().referenceLine?.lengthMeters ?? null;
}
